import {gql} from '@apollo/client';

import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';

import {
  EntityMap,
  EntityMap_entityMap_EntityMapNode as EntityMapNode,
  EntityMapVariables,
} from './__generated/EntityMap';

export const ENTITY_MAP_QUERY = gql`
  query EntityMap($entityMapInput: EntityMapInputNode!) @skipAudit @engComponent(name: REGISTRY) {
    entityMap(entityMapInput: $entityMapInput) {
      ... on EntityMapNode {
        entities {
          ... on EntityMapEntityNode {
            id
            schemaId
            schemaName
            edgeCount
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
        edges {
          id
          sourceId
          targetId
        }
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Fetches the entity map graph for a given entityId and depth
 *
 * Throws an error if the data is unavailable or inaccessible.
 * @param entityId  The ID of the node to fetch the entity map for
 * @param depth  The depth of the entity map to fetch
 * @param excludedSchemaIds  Optional schema IDs to exclude from the graph
 */
export async function fetchEntityMap(
  entityId: string,
  depth: number,
  excludedSchemaIds?: Array<string>
): Promise<EntityMapNode> {
  const entityMapInput: EntityMapVariables['entityMapInput'] = {
    entityId,
    depth,
  };
  if (excludedSchemaIds != null && excludedSchemaIds.length > 0) {
    entityMapInput.excludedSchemaIds = excludedSchemaIds;
  }
  const result = await doQuerySafe<EntityMap, EntityMapVariables>(
    {query: ENTITY_MAP_QUERY},
    {variables: {entityMapInput}}
  );

  if (result?.entityMap == null) {
    throw new Error('This data is not available.');
  }

  return result.entityMap;
}
